/**
 * 결과 카드 UI (FR-010/011, US3).
 * 총점·최고 콤보·성적 드립을 SNS 공유형 카드(캔버스)로 표시. 종료 화면은 **웹 페이지 공유**
 * (접속 언어 페이지 URL — 친구가 열면 해당 언어 OG 카드)와 **다시하기**(onReplay) 버튼을 준다.
 */
import type { EventBus } from '../core/EventBus';
import type { ResultCard as ResultCardData } from '../core/types';
import { t } from '../i18n/i18n';
import { shareUrl } from '../config/site';

/** 브랜드명(Web Share 시트 제목 — 로케일 부제는 붙이지 않고 브랜드로 고정). */
const BRAND = 'Desk Smash';
/**
 * 공유할 게임 페이지 URL. 접속 언어의 정적 페이지(en은 루트, ko/ja/zh는 /<loc>/)를 가리켜
 * 크롤러가 해당 언어의 OG 카드를 받도록 한다(언어별 정적 페이지는 빌드 시 생성).
 */
const SHARE_URL = shareUrl();

export class ResultCard {
  private root: HTMLElement;
  private figure: HTMLImageElement;
  private newBestEl: HTMLElement;
  private toastEl: HTMLElement;
  private canvas = document.createElement('canvas');

  constructor(
    parent: HTMLElement,
    bus: EventBus,
    private onReplay: () => void,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'overlay hidden';
    this.root.innerHTML = `
      <div class="card">
        <div class="new-best hidden" id="dr-newbest">${t('newBest')}</div>
        <img class="result-figure" id="dr-figure" alt="${t('title')}" />
        <div class="row">
          <button class="btn" id="dr-share">${t('shareBtn')}</button>
          <button class="btn secondary" id="dr-replay">${t('replayBtn')}</button>
        </div>
        <div class="toast hidden" id="dr-toast"></div>
      </div>`;
    parent.appendChild(this.root);
    this.root.querySelector<HTMLButtonElement>('#dr-replay')!.addEventListener('click', () => {
      this.hide();
      this.onReplay();
    });

    this.figure = this.root.querySelector('#dr-figure')!;
    this.newBestEl = this.root.querySelector('#dr-newbest')!;
    this.toastEl = this.root.querySelector('#dr-toast')!;

    let last: ResultCardData | null = null;
    this.root.querySelector<HTMLButtonElement>('#dr-share')!.addEventListener('click', () => {
      if (last) void this.share(last);
    });

    bus.on('session:end', (e) => {
      last = e.card;
      this.show(e.card);
    });
  }

  private show(card: ResultCardData): void {
    // 성적 드립(quip)은 공유 카드 이미지 안에만 표시(팝업 하단 중복 텍스트 제거).
    this.newBestEl.classList.toggle('hidden', !card.isNewBest);
    this.renderCard(card);
    this.figure.src = this.canvas.toDataURL('image/png');
    this.root.classList.remove('hidden');
  }

  hide(): void {
    this.root.classList.add('hidden');
  }

  /** 공유용 카드 이미지를 canvas에 렌더 (세로 1080×1350). */
  private renderCard(card: ResultCardData): void {
    const W = 1080;
    const H = 1350;
    this.canvas.width = W;
    this.canvas.height = H;
    const ctx = this.canvas.getContext('2d')!;

    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, '#1b1e2b');
    grad.addColorStop(1, '#2a1720');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = 'center';
    ctx.fillStyle = '#ff6b6b';
    ctx.font = 'bold 84px ui-monospace, Menlo, monospace';
    ctx.fillText(t('title'), W / 2, 260);

    ctx.fillStyle = '#f5f6fa';
    ctx.font = 'bold 200px ui-monospace, Menlo, monospace';
    ctx.fillText(card.totalScore.toLocaleString(), W / 2, 620);
    ctx.fillStyle = '#9aa0b0';
    ctx.font = '40px ui-monospace, Menlo, monospace';
    ctx.fillText(t('totalScore'), W / 2, 690);

    ctx.fillStyle = '#ffd43b';
    ctx.font = 'bold 96px ui-monospace, Menlo, monospace';
    ctx.fillText(t('maxCombo', { n: card.maxCombo }), W / 2, 870);

    if (card.isNewBest) {
      ctx.fillStyle = '#ffd43b';
      ctx.font = 'bold 52px ui-monospace, Menlo, monospace';
      ctx.fillText(t('newBestBadge'), W / 2, 955);
    }

    ctx.fillStyle = '#f5f6fa';
    ctx.font = '44px ui-monospace, Menlo, monospace';
    wrapText(ctx, card.quip, W / 2, 1120, W - 160, 60);

    ctx.fillStyle = '#6b7080';
    ctx.font = '32px ui-monospace, Menlo, monospace';
    ctx.fillText('#DeskSmash · smash-desk', W / 2, 1290);
  }

  /** 웹 페이지 공유(가능 시 Web Share API, 아니면 링크 복사). */
  private async share(card: ResultCardData): Promise<void> {
    const text = t('shareText', { score: card.totalScore.toLocaleString(), combo: card.maxCombo });
    const navAny = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (navAny.share) {
      try {
        await navAny.share({ title: BRAND, text, url: SHARE_URL });
        return;
      } catch (err) {
        // 사용자가 취소(AbortError)면 조용히 종료, 그 외 실패는 클립보드 폴백으로.
        if ((err as { name?: string })?.name === 'AbortError') return;
      }
    }
    // 폴백: 링크 클립보드 복사.
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      this.toast(t('linkCopied'));
    } catch {
      this.toast(SHARE_URL);
    }
  }

  private toast(msg: string): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.remove('hidden');
    setTimeout(() => this.toastEl.classList.add('hidden'), 2200);
  }
}

/**
 * 캔버스 텍스트 줄바꿈. 라틴어는 공백 단위로 단어를 보존하고, 공백이 없는
 * 한/일/중 문자열은 글자(코드포인트) 단위로 접어 카드 밖으로 넘치지 않게 한다.
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  let line = '';
  let cy = y;
  const emit = (): void => {
    ctx.fillText(line, x, cy);
    cy += lineHeight;
    line = '';
  };
  for (const ch of text) {
    // 이모지/CJK 안전: for…of는 코드포인트 단위로 순회.
    if (ctx.measureText(line + ch).width > maxWidth && line !== '') {
      const sp = line.lastIndexOf(' ');
      if (sp > 0 && ch !== ' ') {
        // 라틴어: 마지막 공백에서 끊고 진행 중이던 단어는 다음 줄로 넘긴다.
        const carry = line.slice(sp + 1);
        line = line.slice(0, sp);
        emit();
        line = carry + ch;
      } else {
        emit();
        line = ch === ' ' ? '' : ch;
      }
    } else {
      line += ch;
    }
  }
  if (line) ctx.fillText(line, x, cy);
}
